import {
  captureProjectTemplate,
  rotateTemplateClipboard,
  templateCircuitType,
  templateGridMismatchMessage,
} from '@/services/ProjectTemplateService';
import { canEditPlayerChip } from '@/challenges/playerChipPolicy';
import { restrictChallengeInterfacePaste } from '@/challenges/clipboardInterfaces';
import { orderedEdit, orderedEditResult } from '@/simulation/native/authority';
import { chipGroupSection } from './PaletteExpansionModel';
import { clipboardForCircuit } from '@/utils/structureClipboard';
import { makeAutoObservable, runInAction } from 'mobx';
import type {
  IChipModel,
  IEditorModel,
  IPlaceChipModel,
  IPlaceTileModel,
  ITileManagerModel,
} from '@/interfaces/models';
import Context from '@/context';
import type { ChipId } from '@/types/ids';
import { Rotation, rotateCCW, rotateCW } from '@/models/data/Rotation';
import { TilePlacementStrategyType } from '@/models/grid/TilePlacementStrategy';
import { TileManagerModel } from '@/models/TileManagerModel';
import type { Bounds2D } from '@/utils/bounds';
import type { ClipboardData } from '@/models/clipboard/ClipboardData';

export enum EditingMode {
  SELECT,
  PAN,
  PLACE_TILE,
  PLACE_CHIP,
  DELETE,
  LABEL,
  PASTE,
  PAINT,
}

export class EditorModel implements IEditorModel {
  editingMode: EditingMode;

  placeTileModel: IPlaceTileModel;
  placeChipModel: IPlaceChipModel;

  currentChip: IChipModel;
  openChipIds: ChipId[] = [];
  openingChipId: ChipId | null = null;
  chipOpenError = '';
  private chipNavigationGeneration = 0;

  tileRotation = Rotation.CW_0;
  private selectedPlacementStrategy: TilePlacementStrategyType =
    TilePlacementStrategyType.Line;
  get tilePlacementStrategy(): TilePlacementStrategyType {
    return this.selectedPlacementStrategy;
  }
  set tilePlacementStrategy(strategy: TilePlacementStrategyType) {
    this.selectedPlacementStrategy =
      strategy === TilePlacementStrategyType.Ribbon
        ? TilePlacementStrategyType.Line
        : strategy;
  }
  selectionBounds: Bounds2D | null = null;
  selectionDragMessage = '';
  selectionDragStacking = false;
  selectionDragBounds: Bounds2D | null = null;
  clipboardData: ClipboardData | null = null;
  pendingPasteData: ClipboardData | null = null;
  pendingTemplateId: ChipId | null = null;
  templatePlacementMessage = '';
  pendingTemplateType: 'logic' | 'structure' | null = null;
  pastePreviewTileManager: ITileManagerModel;

  constructor(
    private context: Context,
    {
      tileSelectModel,
      chipSelectModel,
      currentChip,
      editingMode = EditingMode.PAN,
      tilePlacementStrategy = TilePlacementStrategyType.Line,
    }: {
      tileSelectModel: IPlaceTileModel;
      chipSelectModel: IPlaceChipModel;
      currentChip: IChipModel;
      editingMode?: EditingMode;
      tilePlacementStrategy?: TilePlacementStrategyType;
    },
  ) {
    this.placeTileModel = tileSelectModel;
    this.placeChipModel = chipSelectModel;
    this.currentChip = currentChip;
    this.openChipIds = [currentChip.id];
    this.editingMode = editingMode;
    this.tilePlacementStrategy = tilePlacementStrategy;
    this.pastePreviewTileManager = new TileManagerModel(context);
    makeAutoObservable<this, 'chipNavigationGeneration'>(
      this,
      { chipNavigationGeneration: false },
      { autoBind: true },
    );

    if (typeof this.placeTileModel.bindEditor === 'function') {
      this.placeTileModel.bindEditor(this);
    }
    if (typeof this.placeChipModel.bindEditor === 'function') {
      this.placeChipModel.bindEditor(this);
    }
  }

  setEditingMode(editingMode: EditingMode) {
    if (editingMode !== EditingMode.PASTE && this.pendingTemplateId) {
      this.pendingTemplateId = null;
      this.pendingTemplateType = null;
      this.pendingPasteData = null;
      this.templatePlacementMessage = '';
    }
    this.editingMode = editingMode;
  }

  isChipGroupExpanded(group: string) {
    return this.context.userPreferences.paletteExpansion.isExpanded(
      chipGroupSection(group),
    );
  }

  toggleChipGroup(group: string) {
    this.context.userPreferences.paletteExpansion.toggle(chipGroupSection(group));
  }

  setCurrentChip(chip: IChipModel) {
    return orderedEdit(this.context, () => this.setCurrentChipNow(chip));
  }

  async setCurrentChipAsync(
    chip: IChipModel,
    signal?: AbortSignal,
    beforeNavigation?: () => void,
  ): Promise<void> {
    signal?.throwIfAborted();
    const queuedGeneration = this.chipNavigationGeneration;
    await orderedEditResult(this.context, () => {
      signal?.throwIfAborted();
      beforeNavigation?.();
      if (queuedGeneration !== this.chipNavigationGeneration)
        throw new DOMException('Chip navigation changed while queued.', 'AbortError');
      return this.setCurrentChipNow(chip, signal, beforeNavigation);
    });
    signal?.throwIfAborted();
    if (this.chipOpenError) throw new Error(this.chipOpenError);
    const source =
      this.context.app?.getSourceChip?.(this.currentChip) ?? this.currentChip;
    if (
      this.chipNavigationGeneration !== queuedGeneration + 1 ||
      source.id !== chip.id ||
      this.openingChipId
    )
      throw new DOMException('Chip navigation changed before completion.', 'AbortError');
  }

  private setCurrentChipNow(
    chip: IChipModel,
    signal?: AbortSignal,
    beforeApply?: () => void,
  ) {
    const navigation = ++this.chipNavigationGeneration;
    const source = this.context.app?.getSourceChip?.(chip) ?? chip;
    if (
      this.context.challenge &&
      source.id !== this.context.mainChip.id &&
      !canEditPlayerChip(this.context, source)
    )
      return;
    if (!this.openChipIds.includes(source.id)) this.openChipIds.push(source.id);
    this.chipOpenError = '';
    if (
      source.id !== this.context.mainChip?.id &&
      (source.logicCircuit?.circuitModel?.tileManagerModel?.tiles.length ?? 0) >= 1000 &&
      this.context.app?.getEditingChipAsync
    ) {
      this.openingChipId = source.id;
      return this.context.app
        .getEditingChipAsync(
          source,
          () => navigation === this.chipNavigationGeneration && !signal?.aborted,
        )
        .then((next) => {
          if (navigation !== this.chipNavigationGeneration) return;
          signal?.throwIfAborted();
          beforeApply?.();
          runInAction(() => {
            this.selectionBounds = null;
            if (this.pendingPasteData) this.cancelPaste();
            this.currentChip = next;
            this.openingChipId = null;
          });
        })
        .catch((error) => {
          if (navigation !== this.chipNavigationGeneration) return;
          runInAction(() => {
            this.openingChipId = null;
            if (error?.name !== 'AbortError')
              this.chipOpenError =
                error instanceof Error ? error.message : 'Unable to open chip.';
          });
        });
    }
    this.openingChipId = null;
    const next = this.context.app?.getEditingChip?.(chip) ?? chip;
    if (next !== this.currentChip) {
      this.selectionBounds = null;
      if (this.pendingPasteData) this.cancelPaste();
    }
    this.currentChip = next;
  }

  closeChipTab(sourceId: ChipId) {
    if (sourceId === this.context.mainChip.id) return;
    if (this.openingChipId === sourceId) {
      this.chipNavigationGeneration++;
      this.openingChipId = null;
    }
    if (
      this.context.app?.chipDrafts &&
      [...this.context.app.chipDrafts.values()].some(
        (entry) => entry.source.id === sourceId,
      )
    ) {
      throw new Error('Save or discard the chip draft before closing its tab.');
    }
    const index = this.openChipIds.indexOf(sourceId);
    if (index < 0) return;
    this.openChipIds.splice(index, 1);
    const source =
      this.context.app?.getSourceChip?.(this.currentChip) ?? this.currentChip;
    if (source.id === sourceId) {
      const nextId = this.openChipIds[Math.max(0, index - 1)];
      this.setCurrentChip(
        this.context.chipStore.getChip(nextId) ?? this.context.mainChip,
      );
    }
  }

  rotateTileSelectionCW() {
    if (this.editingMode === EditingMode.PASTE && this.pendingPasteData) {
      this.pendingPasteData = rotateTemplateClipboard(this.pendingPasteData);
      this.tileRotation = rotateCW(this.tileRotation);
      return;
    }
    this.tileRotation = rotateCW(this.tileRotation);
  }

  rotateTileSelectionCCW() {
    if (this.editingMode === EditingMode.PASTE && this.pendingPasteData) {
      this.pendingPasteData = rotateTemplateClipboard(this.pendingPasteData, false);
      this.tileRotation = rotateCCW(this.tileRotation);
      return;
    }
    this.tileRotation = rotateCCW(this.tileRotation);
  }

  setTilePlacementStrategy(strategy: TilePlacementStrategyType) {
    this.tilePlacementStrategy = strategy;
  }

  selectProjectTemplate(chip: IChipModel) {
    if (!chip.templateType || this.context.challenge) return;
    const data = captureProjectTemplate(chip);
    this.tileRotation = Rotation.CW_0;
    this.pendingTemplateId = chip.id;
    this.pendingTemplateType = chip.templateType;
    this.pendingPasteData = data;
    this.templatePlacementMessage = !data
      ? 'This template is empty. Edit it before placing.'
      : this.currentCircuit.circuitType !== templateCircuitType(chip.templateType)
        ? templateGridMismatchMessage(chip.templateType)
        : '';
    this.editingMode = EditingMode.PASTE;
  }

  enterPasteMode(data: ClipboardData) {
    this.pendingTemplateId = null;
    this.pendingTemplateType = null;
    this.templatePlacementMessage = '';
    const normalized = clipboardForCircuit(
      data,
      this.currentCircuit.circuitType,
      this.currentChip.kind === 'bundle-adapter',
    );
    const prepared = restrictChallengeInterfacePaste(this.context, normalized);
    if (prepared !== normalized && !prepared.tiles.length) {
      if (this.pendingPasteData) this.cancelPaste();
      return;
    }
    this.pendingPasteData = prepared;
    this.editingMode = EditingMode.PASTE;
  }

  cancelPaste() {
    this.pendingTemplateId = null;
    this.pendingTemplateType = null;
    this.templatePlacementMessage = '';
    this.pendingPasteData = null;
    this.editingMode = EditingMode.SELECT;
  }

  setSelectionBounds(bounds: Bounds2D | null) {
    if (!bounds) {
      this.selectionBounds = null;
      return;
    }

    const normalizedBounds: Bounds2D = {
      left: Math.min(bounds.left, bounds.right),
      right: Math.max(bounds.left, bounds.right),
      top: Math.min(bounds.top, bounds.bottom),
      bottom: Math.max(bounds.top, bounds.bottom),
    };

    this.selectionBounds = normalizedBounds;
  }

  get currentChipEditor() {
    return this.context.chipStore.chipEditorMap.get(this.currentChip.id);
  }

  get currentCircuit() {
    const chipEditor = this.currentChipEditor;
    if (!chipEditor) {
      throw new Error('Chip editor not found for current chip');
    }
    return chipEditor.currentCircuitEditor.circuitModel;
  }
}
